import { useCallback } from 'react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { GroupBase } from 'react-select';
import { LoadOptions } from 'react-select-async-paginate';
import Option from '@/components/form/types/Option.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { FilterFieldOperator } from '@/components/table/_filterFieldOperators/FilterFieldOperator.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';

export interface UseAsyncSelectOptionsProps<T> {
  queryKey: QueryKey;
  fetchFn: (
    pageRequest: PageRequest,
    filter: FilterCriteriaDto,
  ) => Promise<PagedDto<T>>;
  toOption: (item: T) => Option;
  pageSize?: number;
  /**
   * Field id used by `fetchOptionById` to resolve the currently selected value
   * back to an option. Defaults to `'id'`. Override when the option value is not
   * the row id (e.g. a consent template picker keyed by `templateId`).
   */
  idFieldId?: string;
  /**
   * `false` while a filter the query depends on is still empty (no client
   * picked yet). The backend rejects an ID filter with a blank value, so the
   * select must not fetch at all instead of fetching with `''`.
   */
  enabled?: boolean;
}

export interface UseAsyncSelectOptionsResult {
  loadOptions: LoadOptions<Option, GroupBase<Option>, { page: number }>;
  fetchOptionById: (id: string) => Promise<Option | null>;
}

const useAsyncSelectOptions = <T>({
  queryKey,
  fetchFn,
  toOption,
  pageSize = 20,
  idFieldId = 'id',
  enabled = true,
}: UseAsyncSelectOptionsProps<T>): UseAsyncSelectOptionsResult => {
  const queryClient = useQueryClient();

  const loadOptions: LoadOptions<
    Option,
    GroupBase<Option>,
    { page: number }
  > = useCallback(
    async (searchTerm, _loadedOptions, additional) => {
      const page = additional?.page ?? 0;

      if (!enabled) {
        return { options: [], hasMore: false, additional: { page } };
      }

      const cacheKey = [...queryKey, 'async-select', searchTerm, page];

      try {
        // Check cache first
        let result = queryClient.getQueryData<PagedDto<T>>(cacheKey);

        if (!result) {
          const filterCriteria: FilterCriteriaDto = {
            filters: [],
            combinator: 'AND',
            searchTerm: searchTerm,
            quickFilters: {},
          };

          result = await fetchFn({ page, size: pageSize }, filterCriteria);
          queryClient.setQueryData(cacheKey, result);
        }

        const newOptions = result.content.map(toOption);
        const hasMore = result.content.length === pageSize;

        return {
          options: newOptions,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        console.error('Error fetching options:', error);
        return {
          options: [],
          hasMore: false,
          additional: { page },
        };
      }
    },
    [queryKey, fetchFn, toOption, pageSize, queryClient, enabled],
  );

  const fetchOptionById = useCallback(
    async (id: string): Promise<Option | null> => {
      const filterCriteria: FilterCriteriaDto = {
        filters: [
          {
            fieldId: idFieldId,
            type: FieldType.ID,
            operator: FilterFieldOperator.EQUALS,
            value: id,
          },
        ],
        combinator: 'AND',
        searchTerm: '',
        quickFilters: {},
      };
      try {
        const result = await fetchFn({ page: 0, size: 1 }, filterCriteria);
        return result.content.length > 0 ? toOption(result.content[0]) : null;
      } catch (error) {
        console.error('Error resolving option by id:', error);
        return null;
      }
    },
    [fetchFn, toOption, idFieldId],
  );

  return {
    loadOptions,
    fetchOptionById,
  };
};

export default useAsyncSelectOptions;
